import moment from "moment-timezone";

/**
 * Smart Menu availability, as POSHUB sends it on every product row and option
 * row of GET /qr-app/product?menu_id= (channel 'qr'):
 *
 *   { is_available, display_state, reason, next_available_at, schedule_name }
 *
 * Rows whose schedule says "hide" are left out of the response altogether, so
 * the app only ever sees available or unavailable rows here. POSHUB accepts an
 * order for an unavailable row (it only logs a warning), so stopping that order
 * is up to this app -- the same split as the webshop and the kiosk.
 */

const DEFAULT_TIMEZONE = "Europe/Amsterdam";

function timezone() {
  return process.env.VUE_APP_TIMEZONE || DEFAULT_TIMEZONE;
}

function restaurantNow(now) {
  return (now ? moment(now) : moment()).tz(timezone());
}

// A missing or malformed block means "no opinion": a backend predating Smart
// Menu sends none, and that must not grey out the whole menu.
export function isAvailable(row) {
  if (!row || !row.availability || typeof row.availability !== "object") {
    return true;
  }
  return row.availability.is_available !== false;
}

// POSHUB can report stock through the same block. That case keeps the app's
// own "Out of Stock" wording instead of a schedule label.
export function isOutOfStockByAvailability(row) {
  return Boolean(
    row && row.availability && row.availability.display_state === "out_of_stock"
  );
}

/**
 * The schedule's own text for an unavailable row, in the given locale, or null
 * when the schedule has none. POSHUB sends it as text_mode 'custom' with `text`
 * and, optionally, `text_translations` keyed by locale; a backend without that
 * change sends neither, which falls through to the standard text.
 */
export function customAvailabilityText(availability, locale) {
  if (!availability || availability.text_mode !== "custom") {
    return null;
  }
  const translations = availability.text_translations;
  const translated =
    translations && typeof translations === "object" ? translations[locale] : null;
  if (typeof translated === "string" && translated.trim()) {
    return translated.trim();
  }
  if (typeof availability.text === "string" && availability.text.trim()) {
    return availability.text.trim();
  }
  return null;
}

/**
 * The label for an unavailable row, or null when it is available: the
 * schedule's custom text when it has one, otherwise "Niet beschikbaar".
 *
 * @param {Object} row product row or option row
 * @param {Function} t vue-i18n translate function ($t)
 * @param {string} locale the active vue-i18n locale
 */
export function availabilityLabel(row, t, locale) {
  if (isAvailable(row)) {
    return null;
  }
  return customAvailabilityText(row.availability, locale) || t("availability.unavailable");
}

// The option rows of one grouped product row, flattened.
export function optionRowsOf(row) {
  const groups = (row && row.menu_product && row.menu_product.optional_groups) || [];
  const options = [];
  groups.forEach((group) => {
    (group.optional_group || []).forEach((optionalGroup) => {
      (optionalGroup.products || []).forEach((option) => options.push(option));
    });
  });
  return options;
}

function productRowsOf(groups) {
  const rows = [];
  (groups || []).forEach((group) => {
    (group.products || []).forEach((row) => rows.push(row));
  });
  return rows;
}

/**
 * The earliest next_available_at in the loaded menu that is still in the
 * future (products and options), as a moment, or null. Menu.vue refetches
 * shortly after it so an opening shows up without a reload.
 */
export function earliestNextAvailableAt(groups, now) {
  const nowTz = restaurantNow(now);
  let earliest = null;
  const consider = (row) => {
    if (isAvailable(row) || !row.availability.next_available_at) {
      return;
    }
    const at = moment(row.availability.next_available_at);
    if (at.isValid() && at.isAfter(nowTz) && (!earliest || at.isBefore(earliest))) {
      earliest = at;
    }
  };
  productRowsOf(groups).forEach((row) => {
    consider(row);
    optionRowsOf(row).forEach(consider);
  });
  return earliest;
}

// The key a cart line carries: dataTypes/product.js stores
// `article_number || id` of the product it was built from.
function articleKey(product) {
  if (!product) {
    return null;
  }
  const key = product.article_number ? product.article_number : product.id;
  return key === undefined || key === null ? null : String(key);
}

/**
 * Article keys -> "is any visible row with this article available".
 * The same article can sit in two categories with different schedules (the
 * category's schedule applies when the product has none of its own), so a line
 * is only unavailable if NO visible row of that article is available.
 *
 * customTexts keeps, per article, the custom-text fields of the first
 * unavailable row that has them, so a cart line can show the same text as
 * the menu.
 */
export function buildAvailabilityIndex(groups) {
  const products = {};
  const options = {};
  const customTexts = { products: {}, options: {} };
  const mark = (index, texts, key, row) => {
    if (key === null) {
      return;
    }
    const available = isAvailable(row);
    index[key] = Boolean(index[key]) || available;
    if (!available && !texts[key] && row.availability.text_mode === "custom") {
      texts[key] = {
        text_mode: "custom",
        text: row.availability.text,
        text_translations: row.availability.text_translations,
      };
    }
  };
  productRowsOf(groups).forEach((row) => {
    const product = row.menu_product && row.menu_product.product;
    mark(products, customTexts.products, articleKey(product), row);
    optionRowsOf(row).forEach((option) => {
      mark(options, customTexts.options, articleKey(option), option);
    });
  });
  return { products, options, customTexts };
}

/**
 * The article keys in the cart that cannot be ordered right now: absent from
 * the menu (hidden, withdrawn) or present only as unavailable rows.
 *
 * @param {Array} cartProducts state.cart.products
 * @param {Array} groups the grouped menu (product/groupedData shape)
 * @return {{ products: string[], options: string[], customTexts: Object }}
 */
export function findBlockedArticles(cartProducts, groups) {
  const index = buildAvailabilityIndex(groups);
  const products = new Set();
  const options = new Set();
  (cartProducts || []).forEach((line) => {
    const key = articleKey(line);
    if (key !== null && index.products[key] !== true) {
      products.add(key);
    }
    (line.optionalGroups || []).forEach((option) => {
      const optionKey = articleKey(option);
      if (optionKey !== null && index.options[optionKey] !== true) {
        options.add(optionKey);
      }
    });
  });
  const customTexts = { products: {}, options: {} };
  products.forEach((key) => {
    if (index.customTexts.products[key]) {
      customTexts.products[key] = index.customTexts.products[key];
    }
  });
  options.forEach((key) => {
    if (index.customTexts.options[key]) {
      customTexts.options[key] = index.customTexts.options[key];
    }
  });
  return { products: [...products], options: [...options], customTexts };
}

/**
 * Whether one cart line is blocked by a findBlockedArticles() result: the
 * product itself, or any option on it.
 */
export function isCartLineBlocked(line, blocked) {
  if (!line || !blocked) {
    return false;
  }
  const productKeys = blocked.products || [];
  const optionKeys = blocked.options || [];
  if (productKeys.includes(articleKey(line))) {
    return true;
  }
  return (line.optionalGroups || []).some((option) =>
    optionKeys.includes(articleKey(option))
  );
}

/**
 * The label for a blocked cart line: the custom text of its product, else of
 * the first blocked option that has one, else "Niet beschikbaar". An article
 * that is gone from the menu has no schedule text and gets the standard one.
 */
export function cartLineLabel(line, blocked, t, locale) {
  const texts = (blocked && blocked.customTexts) || {};
  const productTexts = texts.products || {};
  const optionTexts = texts.options || {};
  const optionKeys = (blocked && blocked.options) || [];
  const candidates = [productTexts[articleKey(line)]];
  ((line && line.optionalGroups) || []).forEach((option) => {
    const key = articleKey(option);
    if (optionKeys.includes(key)) {
      candidates.push(optionTexts[key]);
    }
  });
  for (let i = 0; i < candidates.length; i += 1) {
    const text = customAvailabilityText(candidates[i], locale);
    if (text) {
      return text;
    }
  }
  return t("availability.unavailable");
}
